import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useMatch,
  useNavigate,
  useParams,
} from "react-router-dom";
import LocationSelectionPage from "../modules/locationSelection/LocationSelectionPage";
import OrdersPage from "../modules/orders/OrdersPage";
import AcceptOrderPage from "../modules/orders/AcceptOrderPage";
import routes from "./routes";
import { useOrderDetailsMatch } from "./useOrderDetailsMatch";
import LoaderOverlay from "../modules/layout/components/LoaderOverlay";
import React, { Suspense, useEffect } from "react";
import { BookLocationDialog as EquipmentRentalBookLocationDialog } from "../modules/bookLocation/equipmentRental/BookLocationDialog";
import { BookLocationDialog as SUPBookLocationDialog } from "../modules/bookLocation/sup/BookLocationDialog";
import { BookLocationDialog as P2PBookLocationDialog } from "../modules/bookLocation/p2p/BookLocationDialog";
import { BookLocationDialog as ParcelBookLocationDialog } from "../modules/bookLocation/parcel/BookLocationDialog";
import HintsPage from "../modules/hints/HintsPage";
import StripePaymentReturnPage from "../modules/payments/StripePaymentReturnPage";
import ConsentPage from "../modules/consent/ConsentPage";
import { useSelector } from "react-redux";
import { RootState } from "../redux/store";
import NoPaymentReturnPage from "../modules/payments/NoPaymentReturnPage";
import HomePage from "../modules/home/homePage";
import ItemRetrievalPage from "../modules/bookLocation/p2p/itemRetrieval/ItemRetrievalPage";
import SenderRetrievalPage from "../modules/bookLocation/p2p/senderItemPlacement/SenderItemPlacementPage";
import { TempStorageBookLocationDialog } from "../modules/bookLocation/temporaryStorage/TempStorageBookLocationDialog";
import { RoomRentalDialog } from "../modules/bookLocation/roomRental/RoomRentalDialog";
import MoreToComeDialog from "../modules/moreToCome/MoreToComeDialog";
import { VenueReservationDialog } from "../modules/bookLocation/venueReservation/venueReservationDialog";
import { VoucherPurchaseDialog } from "../modules/bookLocation/voucher/dialog/VoucherPurchaseDialog";
import VoucherStripePaymentReturnPage from "../modules/payments/VoucherStripePaymentReturnPage";
import { KeyHandoverBookingDialog } from "../modules/bookLocation/keyHandover/dialog/KeyHandoverBookingDialog";
import KeyHandoverLocationSelectionPage from "../modules/bookLocation/keyHandover/pages/KeyHandoverLocationSelectionPage";
import KeyHandoverPaymentModelPage from "../modules/bookLocation/keyHandover/pages/KeyHandoverPaymentModelPage";
import KeyHandoverSubscriptionChoicePage from "../modules/bookLocation/keyHandover/pages/KeyHandoverSubscriptionChoicePage";
import KeyHandoverServiceSelectionPage from "../modules/bookLocation/keyHandover/pages/KeyHandoverServiceSelectionPage";
import SubscriptionPlanSelectionPage from "../modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionPlanSelectionPage";
import SubscriptionEmailInputPage from "../modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionEmailInputPage";
import SubscriptionVerifyCodePage from "../modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionVerifyCodePage";
import SubscriptionHostDataPage from "../modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionHostDataPage";
import SubscriptionPaymentPage from "../modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionPaymentPage";
import SubscriptionLoginPage from "../modules/bookLocation/keyHandover/pages/existingSubscription/SubscriptionLoginPage";
import SubscriptionManagePage from "../modules/bookLocation/keyHandover/pages/existingSubscription/SubscriptionManagePage";
import KeyHandoverSchedulePage from "../modules/bookLocation/keyHandover/pages/KeyHandoverSchedulePage";
import KeyHandoverReturnSchedulePage from "../modules/bookLocation/keyHandover/pages/KeyHandoverReturnSchedulePage";
import KeyHandoverHostDetailsPage from "../modules/bookLocation/keyHandover/pages/KeyHandoverHostDetailsPage";
import KeyHandoverGuestDetailsPage from "../modules/bookLocation/keyHandover/pages/KeyHandoverGuestDetailsPage";
import KeyHandoverBookingSummaryPage from "../modules/bookLocation/keyHandover/pages/KeyHandoverBookingSummaryPage";
import SubscriptionSummaryPage from "../modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionSummaryPage";
import KeyHandoverPaymentPage from "../modules/bookLocation/keyHandover/pages/KeyHandoverPaymentPage";
import SubscriptionSuccessPage from "modules/bookLocation/keyHandover/pages/newSubscription/SubscriptionSuccessPage";
import SubscriptionPaymentReturnPage from "modules/payments/SubscriptionPaymentReturnPage";
import RequireService from "./components/RequireService";
import RequireAccountsEnabled from "../modules/account/components/RequireAccountsEnabled";
import RequireAccount from "../modules/account/components/RequireAccount";
import AccountLoginPage from "../modules/account/pages/AccountLoginPage";
import AccountLoginVerifyPage from "../modules/account/pages/AccountLoginVerifyPage";
import AccountPage from "../modules/account/pages/AccountPage";
import OrganizationMembersPage from "../modules/account/pages/OrganizationMembersPage";
import OrganizationBookingsPage from "../modules/account/pages/OrganizationBookingsPage";
import OrganizationSeriesPage from "../modules/account/pages/OrganizationSeriesPage";
import OrganizationHallTimesPage from "../modules/account/pages/OrganizationHallTimesPage";
import RoomWeekPage from "../modules/roomWeek/pages/RoomWeekPage";
import PublicPlanPage from "../modules/roomWeek/pages/PublicPlanPage";
import { consentRequiredFirst } from "./consentExempt";

const HintsPageDialog = React.lazy(
  () =>
    import(
      "../modules/hints/components/DialogComponent" /* webpackChunkName: "tips-dialog" */
    )
);
const UseOrderDialog = React.lazy(
  () =>
    import(
      "../modules/useOrder/UseOrderDialog" /* webpackChunkName: "tips-dialog" */
    )
);
const ReportIssueDialog = React.lazy(
  () =>
    import(
      "../modules/reportIssue/ReportIssueDialog" /* webpackChunkName: "tips-dialog" */
    )
);
const RetrieveBookingViaEmailDialog = React.lazy(
  () =>
    import(
      "../modules/bookLocation/equipmentRental/RetrieveBookingViaEmailDialog" /* webpackChunkName: "tips-dialog" */
    )
);

const RedirectToConsent: React.FC = () => {
  const location = useLocation();
  const originalURL = location.pathname + location.search;

  return (
    <Navigate
      to={`${routes.consentGDPRPagePath}?redirect=${encodeURIComponent(
        originalURL
      )}`}
      replace
    />
  );
};

interface PrivacyPageWrapperProps {
  open: boolean;
}

const PrivacyPageWrapper: React.FC<PrivacyPageWrapperProps> = ({ open }) => {
  const { privcayPage } = useParams();

  if (privcayPage) {
    if (privcayPage.toLowerCase().includes("dataprotection")) {
      return <HintsPageDialog open={open} />;
    }
  }
  return <Navigate to={routes.consentGDPRPagePath} />;
};

const PageRoutes = () => {
  const locationEquipmentRentalRouteMatch = useMatch({
    path: routes.bookLocationEquipmentRentalPath,
  });
  const roomRentalRouteMatch = useMatch({
    path: routes.roomRentalPath,
  });
  const supRentalRouteMatch = useMatch({
    path: routes.bookLocationSUPPath,
  });
  const venueReservationRouteMatch = useMatch({
    path: routes.venueReservationPath,
  });
  const hallReservationRouteMatch = useMatch({
    path: routes.hallReservationPath,
  });
  const keyHandoversRouteMatch = useMatch({
    path: routes.bookLocationKeysPath,
  });
  const locationP2PRouteMatch = useMatch({
    path: routes.bookLocationP2PPath,
  });
  const locationParcelRouteMatch = useMatch({
    path: routes.bookParcelDeliveryPath,
  });
  const locationParcelPlacementRouteMatch = useMatch({
    path: routes.parcelItemPlacementPath,
  });
  const locationKeyRetrievalRouteMatch = useMatch({
    path: routes.keyRetrievalPath,
    });
  const locationParcelItemRetrievalRouteMatch = useMatch({
    path: routes.parcelItemRetrievalPath,
  });
  const locationTemStorageNewBookingRouteMatch = useMatch({
    path: routes.temStorageNewBooking,
  });
  const giftCardPurchaseRouteMatch = useMatch({
    path: routes.voucherPath,
  });
  const showMoreToComeDialog = Boolean(
    useMatch({ path: routes.moreToComePath })
  );

  // #2352: guarded matcher — plain `useMatch(orderDetailsPath)` also matches
  // the postpay acceptance link `/orders/accept` and captures the literal
  // segment as an order id. See useOrderDetailsMatch.ts.
  const orderDetailsMatch = useOrderDetailsMatch();
  const retrieveDetailsMatch = Boolean(
    useMatch({ path: routes.retrieveBookingEmailPath })
  );
  // const reportDamageMatch = useMatch(Routes.reportDamagePath);
  // const reportIssueMatch = useMatch(Routes.reportIssuePath);
  // const orderFaqsMatch = useMatch(Routes.orderFaqsPath);
  // const orderRouteMatch = orderDetailsMatch || reportDamageMatch || reportIssueMatch || orderFaqsMatch;
  const orderRouteMatch = orderDetailsMatch;

  const showHintsPageDialog = Boolean(
    useMatch({ path: routes.hintsPagePath, end: true })
  );
  const dataPrivacyGDPRMatch = useMatch({
    path: routes.privacyGDPRPath,
    end: true,
  });
  const showReportIssueDialog = Boolean(
    useMatch({ path: routes.reportIssuePath })
  );

  const showBookLocationP2PDialog = Boolean(
    locationP2PRouteMatch &&
      locationP2PRouteMatch.pathname === routes.bookLocationP2PPath
  );
  const showBookLocationParcelDialog = Boolean(
    locationParcelRouteMatch &&
      locationParcelRouteMatch.pathname === routes.bookParcelDeliveryPath
  );
  const showBookLocationParcelPlacementDialog = Boolean(
    locationParcelPlacementRouteMatch &&
      locationParcelPlacementRouteMatch.pathname === routes.parcelItemPlacementPath
  );
  const showBookLocationKeyRetrievalDialog = Boolean(
    locationKeyRetrievalRouteMatch &&
      locationKeyRetrievalRouteMatch.pathname === routes.keyRetrievalPath
       );
  const showBookLocationParcelRetrievalDialog = Boolean(
    locationParcelItemRetrievalRouteMatch &&
      locationParcelItemRetrievalRouteMatch.pathname === routes.parcelItemRetrievalPath
  );
  const showBookLocationTempStorageDialog = Boolean(
    locationTemStorageNewBookingRouteMatch &&
      locationTemStorageNewBookingRouteMatch.pathname ===
        routes.temStorageNewBooking
  );
  const showBookLocationEquipmentRentalDialog = Boolean(
    locationEquipmentRentalRouteMatch &&
      locationEquipmentRentalRouteMatch.pathname ===
        routes.bookLocationEquipmentRentalPath
  );
  const showRoomRentalDialog = Boolean(
    roomRentalRouteMatch &&
      roomRentalRouteMatch.pathname === routes.roomRentalPath
  );
  const voucherPurchaseDialog = Boolean(
    giftCardPurchaseRouteMatch &&
      giftCardPurchaseRouteMatch.pathname === routes.voucherPath
  );
  const showSupRentalDialog = Boolean(
    supRentalRouteMatch &&
      supRentalRouteMatch.pathname === routes.bookLocationSUPPath
  );
  const showVenueReservationDialog = Boolean(
    venueReservationRouteMatch &&
      venueReservationRouteMatch.pathname === routes.venueReservationPath
  );
  const showHallReservationDialog =
    hallReservationRouteMatch?.pathname === routes.hallReservationPath;
  const showKeyHandoverDialog = Boolean(
    keyHandoversRouteMatch &&
    keyHandoversRouteMatch.pathname === routes.bookLocationKeysPath
  );
  const showUseOrderDialog = Boolean(orderRouteMatch);
  const showDataPrivacyDialog = Boolean(dataPrivacyGDPRMatch);

  const consentLoaded = useSelector(
    (state: RootState) => state.consent.consentLoaded
  );
  const consent = useSelector((state: RootState) => state.consent.consent);
  const tenantConfig = useSelector(
    (state: RootState) => state.tenantConfig.config
  );
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!consentLoaded) return;

    // Don't redirect if already on the consent page or on the privacy sub-route
    // that the consent page links to — otherwise the user bounces back before the
    // route can render.
    if (
      location.pathname === routes.consentGDPRPagePath ||
      location.pathname.startsWith(`${routes.consentGDPRPagePath}/`)
    ) {
      return;
    }

    const originalURL = location.pathname + location.search;
    if (
      consentRequiredFirst({
        consent,
        consentRequired: tenantConfig.consentRequired,
        pathname: location.pathname,
      })
    ) {
      navigate(
        `${routes.consentGDPRPagePath}?redirect=${encodeURIComponent(
          originalURL
        )}`,
        { replace: true }
      );
    }
  }, [consent, consentLoaded, navigate, location.pathname, location.search, tenantConfig.consentRequired]);

  if (!consentLoaded) {
    return <LoaderOverlay open={!consentLoaded} />;
  }

  if (
    consentRequiredFirst({
      consent,
      consentRequired: tenantConfig.consentRequired,
      pathname: location.pathname,
    })
  ) {
    return (
      <>
        <Routes>
          <Route path={routes.consentGDPRPagePath} element={<ConsentPage />} />
          <Route
            path={routes.privacyGDPRPath}
            element={<PrivacyPageWrapper open={showDataPrivacyDialog} />}
          />
          <Route path="*" element={<RedirectToConsent />} />
        </Routes>
        <Suspense
          fallback={
            showDataPrivacyDialog ? <LoaderOverlay open={true} /> : null
          }
        >
          <HintsPageDialog open={showDataPrivacyDialog} />
        </Suspense>
      </>
    );
  }
  return (
    <>
      <Routes>
        {/* #1874: OrdersPage itself is account-aware — logged in it sources
            its order ids from GET /me/bookings (see useAccountOrderIdSource). */}
        {/* #1702: registered BEFORE orderDetailsPath so the literal `accept`
            segment is never captured as an `:orderId`. */}
        <Route path={routes.acceptOrderPath} element={<AcceptOrderPage />} />
        <Route path={routes.orders} element={<OrdersPage />} />
        <Route path={routes.orderDetailsPath} element={<OrdersPage />} />
        <Route path={routes.hints} element={<HintsPage />} />
        <Route path={routes.home} element={<HomePage />} />
        <Route path={routes.locations} element={<LocationSelectionPage />} />
        {/* H-3a account (email + OTP login) — dark until accountsEnabled */}
        <Route
          path={routes.accountLoginPath}
          element={
            <RequireAccountsEnabled>
              <AccountLoginPage />
            </RequireAccountsEnabled>
          }
        />
        <Route
          path={routes.accountLoginVerifyPath}
          element={
            <RequireAccountsEnabled>
              <AccountLoginVerifyPage />
            </RequireAccountsEnabled>
          }
        />
        <Route
          path={routes.accountPath}
          element={
            <RequireAccountsEnabled>
              <RequireAccount>
                <AccountPage />
              </RequireAccount>
            </RequireAccountsEnabled>
          }
        />
        {/* #2163 — the account's delegated-admin scope. Same guards as the
            account page itself: the surface is dark without accounts, and
            requires a session. Whether this particular person may administer
            THIS organization is decided server-side; an organization they do
            not administer answers 404 and the page says so. */}
        <Route
          path={routes.accountOrganizationMembersPath}
          element={
            <RequireAccountsEnabled>
              <RequireAccount>
                <OrganizationMembersPage />
              </RequireAccount>
            </RequireAccountsEnabled>
          }
        />
        <Route
          path={routes.accountOrganizationBookingsPath}
          element={
            <RequireAccountsEnabled>
              <RequireAccount>
                <OrganizationBookingsPage />
              </RequireAccount>
            </RequireAccountsEnabled>
          }
        />
        <Route
          path={routes.accountOrganizationSeriesPath}
          element={
            <RequireAccountsEnabled>
              <RequireAccount>
                <OrganizationSeriesPage />
              </RequireAccount>
            </RequireAccountsEnabled>
          }
        />
        <Route
          path={routes.accountOrganizationHallTimesPath}
          element={
            <RequireAccountsEnabled>
              <RequireAccount>
                <OrganizationHallTimesPage />
              </RequireAccount>
            </RequireAccountsEnabled>
          }
        />
        <Route
          path={routes.keyHandoverLocationSelectionPath}
          element={
            <RequireService service="keys">
              <KeyHandoverLocationSelectionPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverPaymentModelPath}
          element={
            <RequireService service="keys">
              <KeyHandoverPaymentModelPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionChoicePath}
          element={
            <RequireService service="keys">
              <KeyHandoverSubscriptionChoicePage />
            </RequireService>
          }
        />
        {/* Flow 2: New Subscription Routes */}
        <Route
          path={routes.keyHandoverSubscriptionPlanSelectionPath}
          element={
            <RequireService service="keys">
              <SubscriptionPlanSelectionPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionEmailInputPath}
          element={
            <RequireService service="keys">
              <SubscriptionEmailInputPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionVerifyCodePath}
          element={
            <RequireService service="keys">
              <SubscriptionVerifyCodePage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionHostDataPath}
          element={
            <RequireService service="keys">
              <SubscriptionHostDataPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionSummaryPath}
          element={
            <RequireService service="keys">
              <SubscriptionSummaryPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionPaymentPath}
          element={
            <RequireService service="keys">
              <SubscriptionPaymentPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionSuccessPath}
          element={
            <RequireService service="keys">
              <SubscriptionSuccessPage />
            </RequireService>
          }
        />
        {/* Flow 3: Existing Subscription Routes */}
        <Route
          path={routes.keyHandoverSubscriptionLoginPath}
          element={
            <RequireService service="keys">
              <SubscriptionLoginPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionVerifyPath}
          element={
            <RequireService service="keys">
              <SubscriptionVerifyCodePage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSubscriptionManagePath}
          element={
            <RequireService service="keys">
              <SubscriptionManagePage />
            </RequireService>
          }
        />
        {/* Shared key handover steps */}
        <Route
          path={routes.keyHandoverServiceSelectionPath}
          element={
            <RequireService service="keys">
              <KeyHandoverServiceSelectionPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSchedulePath}
          element={
            <RequireService service="keys">
              <KeyHandoverSchedulePage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverReturnSchedulePath}
          element={
            <RequireService service="keys">
              <KeyHandoverReturnSchedulePage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverHostDetailsPath}
          element={
            <RequireService service="keys">
              <KeyHandoverHostDetailsPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverGuestDetailsPath}
          element={
            <RequireService service="keys">
              <KeyHandoverGuestDetailsPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverSummaryPath}
          element={
            <RequireService service="keys">
              <KeyHandoverBookingSummaryPage />
            </RequireService>
          }
        />
        <Route
          path={routes.keyHandoverPaymentPath}
          element={
            <RequireService service="keys">
              <KeyHandoverPaymentPage />
            </RequireService>
          }
        />
        {/* M3 (#3036): a room's week. Open to signed-out visitors like the
            room lists; what it shows depends on who asks, decided server-side. */}
        <Route path={routes.roomWeekPath} element={<RoomWeekPage />} />
        {/* #3075: the public plan, the same week behind a room dropdown, made
            to sit in an iframe on the tenant's website. */}
        <Route path={routes.publicPlanPath} element={<PublicPlanPage />} />
        {/* Other product routes */}
        <Route
          path={routes.venueReservationPath}
          element={
            <RequireService service="venue">
              <VenueReservationDialog open={showVenueReservationDialog} />
            </RequireService>
          }
        />
        {/* A sports hall runs the venue flow under its own link (PO, 2026-10-18). */}
        <Route
          path={routes.hallReservationPath}
          element={
            <RequireService service="hall">
              <VenueReservationDialog open={showHallReservationDialog} useCase="hall" />
            </RequireService>
          }
        />
        <Route
          path={routes.bookLocationKeysPath}
          element={
            <RequireService service="keys">
              <KeyHandoverBookingDialog open={showKeyHandoverDialog} />
            </RequireService>
          }
        />
        <Route
          path={routes.bookLocationP2PPath}
          element={
            <RequireService service="p2p">
              <P2PBookLocationDialog open={showBookLocationP2PDialog} />
            </RequireService>
          }
        />
        <Route
          path={routes.temStorageNewBooking}
          element={
            <RequireService service="storage">
              <TempStorageBookLocationDialog
                open={showBookLocationTempStorageDialog}
              />
            </RequireService>
          }
        />
        <Route path={routes.p2PItemRetrievalPath} element={<ItemRetrievalPage />} />
        <Route path={routes.p2PSenderRetrievalPath} element={<SenderRetrievalPage />} />
        <Route
          path={routes.bookLocationEquipmentRentalPath}
          element={
            <RequireService service="rental">
              <EquipmentRentalBookLocationDialog
                open={showBookLocationEquipmentRentalDialog}
              />
            </RequireService>
          }
        />
        <Route
          path={routes.roomRentalPath}
          element={
            <RequireService service="apartment">
              <RoomRentalDialog open={showRoomRentalDialog} />
            </RequireService>
          }
        />
        <Route
          path={routes.voucherPath}
          element={
            <RequireService service="voucher">
              <VoucherPurchaseDialog open={voucherPurchaseDialog} />
            </RequireService>
          }
        />
        <Route
          path={routes.bookParcelDeliveryPath}
          element={
            <RequireService service="parcel">
              <ParcelBookLocationDialog open={showBookLocationParcelDialog} />
            </RequireService>
          }
        />
        <Route
          path={routes.bookLocationSUPPath}
          element={
            <RequireService service="sup">
              <SUPBookLocationDialog open={showSupRentalDialog} />
            </RequireService>
          }
        />
        <Route
          path={routes.parcelItemPlacementPath}
          element={
            <ParcelBookLocationDialog open={showBookLocationParcelPlacementDialog} />
          }
        />
        <Route
          path={routes.keyRetrievalPath}
          element={
            <RequireService service="keys">
              <KeyHandoverBookingDialog open={showBookLocationKeyRetrievalDialog} />
            </RequireService>
          }
        />
        <Route
          path={routes.parcelItemRetrievalPath}
          element={<ParcelBookLocationDialog open={showBookLocationParcelRetrievalDialog} />}
        />
        <Route
          path={routes.voucherStripePaymentReturnPath}
          element={<VoucherStripePaymentReturnPage />}
        />
        <Route
          path={routes.stripePaymentReturnPath}
          element={<StripePaymentReturnPage />}
        />
        <Route
          path={routes.subscriptionPaymentReturnPath}
          element={<SubscriptionPaymentReturnPage />}
        />
        <Route path={routes.noPaymentReturnPath} element={<NoPaymentReturnPage />} />
      </Routes>
      <Suspense
        fallback={
          showBookLocationEquipmentRentalDialog ? (
            <LoaderOverlay open={true} />
          ) : null
        }
      >
        {/* <BookLocationDialog
              open={showBookLocationDialog}
          /> */}
      </Suspense>
      <Suspense
        fallback={showHintsPageDialog ? <LoaderOverlay open={true} /> : null}
      >
        <HintsPageDialog open={showHintsPageDialog} />
      </Suspense>
      <Suspense
        fallback={showUseOrderDialog ? <LoaderOverlay open={true} /> : null}
      >
        <UseOrderDialog
          open={showUseOrderDialog && !showReportIssueDialog}
          orderId={orderDetailsMatch?.params.orderId}
        />
        <ReportIssueDialog open={showReportIssueDialog} />
        <RetrieveBookingViaEmailDialog open={retrieveDetailsMatch} />
        <MoreToComeDialog open={showMoreToComeDialog} />
      </Suspense>
    </>
  );
};
export default PageRoutes;
